import { z } from 'zod';

import { kbEntryType, kbSeverity, kbStackLayer } from './kb-types';

/**
 * Form schemas used by `apps/manager-web` with react-hook-form +
 * @hookform/resolvers/zod. Kept in `@repo-manager/shared` so the same
 * shapes can be validated server-side later without redeclaration.
 *
 * Wire payloads (what hits the Worker over the network) live in
 * `api-types.ts`; this file is purely about *form input* and the
 * client-side Supabase row inserts that derive from them.
 */

export const loginForm = z.object({
  email: z.string().email(),
  password: z.string().min(8, 'Password must be at least 8 characters'),
});
export type LoginForm = z.infer<typeof loginForm>;

export const featureCreateForm = z.object({
  title: z.string().min(3).max(120),
  narrative: z.string().min(10),
  success_criteria: z.string().optional(),
  milestone_id: z.string().uuid().nullable().optional(),
  priority: z.coerce.number().int().min(0).max(100).default(0),
});
export type FeatureCreateForm = z.infer<typeof featureCreateForm>;

export const featureUpdateForm = featureCreateForm.partial();
export type FeatureUpdateForm = z.infer<typeof featureUpdateForm>;

export const requirementForm = z.object({
  feature_id: z.string().uuid(),
  type: z.enum(['must', 'never', 'prefer']),
  statement: z.string().min(5),
  rationale: z.string().optional(),
});
export type RequirementForm = z.infer<typeof requirementForm>;

export const epicEditForm = z.object({
  title: z.string().min(3),
  rationale: z.string().min(5),
  kb_refs: z.array(z.string()).default([]),
});
export type EpicEditForm = z.infer<typeof epicEditForm>;

export const storyForm = z.object({
  epic_id: z.string().uuid(),
  title: z.string().min(3),
  description: z.string().optional(),
  acceptance_criteria: z.array(z.string()).default([]),
  effort: z.enum(['small', 'medium', 'large']),
});
export type StoryForm = z.infer<typeof storyForm>;

export const sprintCreateForm = z.object({
  title: z.string().min(3),
  goal: z.string().min(5),
  start_date: z.string().min(1),
  end_date: z.string().min(1),
  story_ids: z.array(z.string().uuid()).min(1, 'Pick at least one story'),
});
export type SprintCreateForm = z.infer<typeof sprintCreateForm>;

export const kbEntryForm = z.object({
  title: z.string().min(3).max(200),
  slug: z
    .string()
    .min(2)
    .regex(/^[a-z0-9-]+$/, 'Lowercase letters, digits, hyphens only'),
  type: kbEntryType,
  severity: kbSeverity,
  summary: z.string().min(10),
  content: z.string().min(20),
  rationale: z.string().optional(),
  code_example: z.string().optional(),
  stack_layers: z.array(kbStackLayer).default([]),
  technologies: z.array(z.string()).default([]),
  workspaces: z.array(z.string()).default([]),
  tags: z.array(z.string()).default([]),
});
export type KbEntryForm = z.infer<typeof kbEntryForm>;
